import React from 'react';
import { Box, Card, CardContent, Grid, Paper, Stack, Typography } from '@mui/material';
import type { FocusedProgressPeriodSummary } from '../../services/types';
import type { GroupExerciseDeltas } from '../../services/exerciseGroupService';

interface GroupDeltasPanelProps {
  deltas: GroupExerciseDeltas[];
}

const PERIODS: Array<{ key: '30d' | '90d' | '180d'; label: string }> = [
  { key: '30d', label: '1 месец' },
  { key: '90d', label: '3 месеца' },
  { key: '180d', label: '6 месеца' },
];

const formatSigned = (value: number | null, unit: string): string => {
  if (value === null || Number.isNaN(value)) return '-';
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}${Math.abs(value)} ${unit}`;
};

const deltaColor = (value: number | null): string => {
  if (value === null || value === 0) return 'text.secondary';
  return value > 0 ? 'success.main' : 'error.main';
};

const formatSessions = (summary: FocusedProgressPeriodSummary): string =>
  `${summary.currentWindowSessionCount}/${summary.previousWindowSessionCount} сесии`;

/**
 * Per-exercise 30/90/180-day rolling deltas for a group. Mirrors the Dashboard
 * "focused progress" widget card layout.
 */
const GroupDeltasPanel: React.FC<GroupDeltasPanelProps> = ({ deltas }) => {
  if (deltas.length === 0) return null;

  return (
    <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
        Прогрес по периоди
      </Typography>
      <Stack spacing={1.5}>
        {deltas.map((item) => (
          <Card key={item.exerciseId} variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {item.variationName || item.exerciseName}
              </Typography>
              <Grid container spacing={1}>
                {PERIODS.map((period) => {
                  const summary = item.summary[period.key];
                  return (
                    <Grid key={period.key} size={{ xs: 12, sm: 4 }}>
                      <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                        <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                          <Stack spacing={0.5}>
                            <Typography variant="caption" color="text.secondary">
                              {period.label}
                            </Typography>
                            {summary.insufficientData ? (
                              <Typography variant="body2" color="text.disabled">Недостатъчно данни</Typography>
                            ) : (
                              <>
                                <Typography variant="body2" sx={{ color: deltaColor(summary.deltaAvgWeightKg) }}>
                                  {formatSigned(summary.deltaAvgWeightKg, 'kg')}
                                </Typography>
                                <Typography variant="body2" sx={{ color: deltaColor(summary.deltaAvgRepsPerSet) }}>
                                  {formatSigned(summary.deltaAvgRepsPerSet, 'повт.')}
                                </Typography>
                                <Typography variant="body2" sx={{ color: deltaColor(summary.deltaAvgRpe) }}>
                                  {formatSigned(summary.deltaAvgRpe, 'RPE')}
                                </Typography>
                              </>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {formatSessions(summary)}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Paper>
  );
};

export default GroupDeltasPanel;
